import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db73lb8d6mcdj80oo7ag';

export default function FeedbackForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // 蜜罐校验：机器人填写则静默返回通用成功提示
    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('感谢反馈，我们会尽快处理。');
      form.reset();
      return;
    }

    setStatus('submitting');
    setMessage('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setMessage('反馈已提交，感谢你的支持！');
        form.reset();
      } else {
        setStatus('error');
        setMessage(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setMessage('网络异常，提交失败，请稍后再试。');
    }
  };

  return (
    <form
      id="douman-feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6"
    >
      <h3 className="mb-1 font-heading text-xl text-foreground-950">反馈与联系</h3>
      <p className="mb-5 text-sm text-foreground-600">
        你的建议是我们前进的动力，欢迎告诉我们想要的功能或发现的问题。
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="nickname" className="mb-1.5 block text-sm text-foreground-700">
            你的称呼
          </label>
          <input
            id="nickname"
            name="nickname"
            type="text"
            required
            placeholder="例如：爱看漫画的我"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm text-foreground-700">
            联系邮箱
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="type" className="mb-1.5 block text-sm text-foreground-700">
          反馈类型
        </label>
        <select
          id="type"
          name="type"
          required
          defaultValue=""
          className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200 cursor-pointer"
        >
          <option value="" disabled>
            请选择反馈类型
          </option>
          <option value="内容建议">内容建议</option>
          <option value="功能需求">功能需求</option>
          <option value="版权问题">版权问题</option>
          <option value="使用问题">使用问题</option>
          <option value="商务合作">商务合作</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="message" className="mb-1.5 block text-sm text-foreground-700">
          反馈内容
        </label>
        <textarea
          id="message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请描述你的想法，最多 500 字"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        />
      </div>

      <input
        className="form-honeypot-field"
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
        >
          <i className="ri-send-plane-line" />
          {status === 'submitting' ? '提交中…' : '提交反馈'}
        </button>
        {message && (
          <p className={`text-sm ${status === 'error' ? 'text-primary-700' : 'text-secondary-700'}`}>{message}</p>
        )}
      </div>
    </form>
  );
}